'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { User } from '@/payload-types'
import {
  INTRANET_PREVIEW_COOKIE_NAME,
  canUserPreviewIntranetRoles,
  isIntranetPreviewValue,
  resolveEffectiveRoles,
  type IntranetPreviewValue,
  type IntranetRole,
  type LoginRole,
} from '@/lib/intranet-preview-shared'

type UsersMeResponse = {
  user?: {
    id?: string | number
    full_name?: string | null
    first_name?: string | null
    last_name?: string | null
    roles?: LoginRole[] | null
    intranet_roles?: IntranetRole[] | null
  } | null
}

type LoginState = {
  isLoading: boolean
  isLoggedIn: boolean
  userId: string | null
  name: string | null
  realRoles: LoginRole[]
  realIntranetRoles: IntranetRole[]
  previewValue: IntranetPreviewValue | null
  error: string | null
}

export type UseLoginStateResult = {
  isLoading: boolean
  isLoggedIn: boolean
  userId: string | null
  name: string | null
  /** Effective `roles` after applying any active intranet preview override. */
  roles: LoginRole[]
  /** Effective `intranet_roles` after applying any active preview override. */
  intranetRoles: IntranetRole[]
  /** Roles as actually stored on the authenticated user. */
  realRoles: LoginRole[]
  /** Intranet roles as actually stored on the authenticated user. */
  realIntranetRoles: IntranetRole[]
  /** Active preview value, or null if no override is engaged. */
  previewValue: IntranetPreviewValue | null
  /** Whether the real user is allowed to engage the intranet role preview. */
  canPreview: boolean
  error: string | null
  refresh: () => Promise<void>
  /**
   * Re-reads the intranet preview cookie. Call after a server action mutates
   * the cookie so role-sensitive UI updates without a hard reload.
   */
  refreshPreview: () => void
}

const initialState: LoginState = {
  isLoading: true,
  isLoggedIn: false,
  userId: null,
  name: null,
  realRoles: [],
  realIntranetRoles: [],
  previewValue: null,
  error: null,
}

const LoginStateContext = createContext<UseLoginStateResult | null>(null)

function getDisplayName(user: UsersMeResponse['user']): string | null {
  if (!user) return null
  if (user.full_name?.trim()) return user.full_name.trim()

  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  return name || null
}

function readIntranetPreviewCookie(): IntranetPreviewValue | null {
  if (typeof document === 'undefined') return null
  const prefix = `${INTRANET_PREVIEW_COOKIE_NAME}=`
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(prefix))
  if (!cookie) return null
  const raw = decodeURIComponent(cookie.slice(prefix.length))
  return isIntranetPreviewValue(raw) ? raw : null
}

type LoginStateProviderProps = {
  children: React.ReactNode
}

export function LoginStateProvider({ children }: LoginStateProviderProps) {
  const [state, setState] = useState<LoginState>(initialState)

  const refresh = useCallback(async () => {
    setState((current) => ({ ...current, isLoading: true, error: null }))

    try {
      const response = await fetch('/api/users/me', {
        method: 'GET',
        cache: 'no-store',
        credentials: 'include',
      })

      if (response.status === 401 || response.status === 403) {
        setState({
          isLoading: false,
          isLoggedIn: false,
          userId: null,
          name: null,
          realRoles: [],
          realIntranetRoles: [],
          previewValue: null,
          error: null,
        })
        return
      }

      if (!response.ok) {
        throw new Error('Unable to check login state.')
      }

      const payload = (await response.json()) as UsersMeResponse
      const user = payload.user ?? null
      const realRoles = (user?.roles ?? []) as LoginRole[]
      const realIntranetRoles = (user?.intranet_roles ?? []) as IntranetRole[]
      const cookieValue = readIntranetPreviewCookie()
      const previewValue = canUserPreviewIntranetRoles(realRoles) ? cookieValue : null

      setState({
        isLoading: false,
        isLoggedIn: Boolean(user),
        userId: user?.id ? String(user.id) : null,
        name: getDisplayName(user),
        realRoles,
        realIntranetRoles,
        previewValue,
        error: null,
      })
    } catch {
      setState({
        isLoading: false,
        isLoggedIn: false,
        userId: null,
        name: null,
        realRoles: [],
        realIntranetRoles: [],
        previewValue: null,
        error: 'Unable to check login state.',
      })
    }
  }, [])

  const refreshPreview = useCallback(() => {
    setState((current) => {
      const cookieValue = readIntranetPreviewCookie()
      const previewValue = canUserPreviewIntranetRoles(current.realRoles) ? cookieValue : null
      if (previewValue === current.previewValue) return current
      return { ...current, previewValue }
    })
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<UseLoginStateResult>(() => {
    const { effectiveRoles, effectiveIntranetRoles } = resolveEffectiveRoles({
      realRoles: state.realRoles,
      realIntranetRoles: state.realIntranetRoles,
      previewValue: state.previewValue,
    })

    return {
      isLoading: state.isLoading,
      isLoggedIn: state.isLoggedIn,
      userId: state.userId,
      name: state.name,
      roles: effectiveRoles,
      intranetRoles: effectiveIntranetRoles,
      realRoles: state.realRoles,
      realIntranetRoles: state.realIntranetRoles,
      previewValue: state.previewValue,
      canPreview: canUserPreviewIntranetRoles(state.realRoles),
      error: state.error,
      refresh,
      refreshPreview,
    }
  }, [refresh, refreshPreview, state])

  return <LoginStateContext.Provider value={value}>{children}</LoginStateContext.Provider>
}

export function useLoginState(): UseLoginStateResult {
  const context = useContext(LoginStateContext)

  if (!context) {
    throw new Error('useLoginState must be used within a LoginStateProvider.')
  }

  return context
}
